import { guardResponse } from "./responseLifecycle";
import { captureSession, invalidateSession, sessionSignal } from "./session";
const API_PORT_STORAGE_KEY = "osim_api_port";

export class ApiError extends Error {
  readonly status: number;
  readonly body: string;
  readonly retryAfterSeconds: number | null;

  constructor(status: number, body: string, retryAfterSeconds: number | null = null) {
    super(body || `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

function normalizeBaseUrl(value: string): string {
  return value.trim().replace(/\/+$/, "");
}

function localBase(value: string): string {
  if (!["localhost", "127.0.0.1"].includes(window.location.hostname)) return "";
  if (!/^\d{1,5}$/.test(value) || Number(value) < 1 || Number(value) > 65535) return "";
  return `${window.location.protocol}//${window.location.hostname}:${Number(value)}`;
}

export function resolveProductionApiBaseUrl(storedOverride: string): string {
  // 旧调用方的 storage 参数保留签名兼容；正式地址由部署配置唯一决定。
  void storedOverride;
  return "";
}

function trustedDeploymentBase(value: string): string {
  try {
    // URL.search/hash 对空分隔符也返回空串，必须在解析前拒绝字面量 ?/#。
    if (/[\\\s?#]/.test(value) || value.startsWith("//")) return "";
    if (!value.startsWith("/") && !/^https?:\/\//.test(value)) return "";
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin || url.username || url.password || url.search || url.hash) return "";
    // 保留完整 origin，避免点段规范化后的 //path 被二次解析成协议相对外域。
    return normalizeBaseUrl(url.href);
  } catch {
    return "";
  }
}

export function resolveApiBaseUrl(): string {
  const envBase = import.meta.env.VITE_API_BASE_URL;
  if (envBase) return trustedDeploymentBase(envBase);

  if (!import.meta.env.DEV) return resolveProductionApiBaseUrl("");

  const override = new URLSearchParams(window.location.search).get("api");
  if (override) {
    const base = localBase(override);
    if (!base) return "";
    try {
      localStorage.setItem(API_PORT_STORAGE_KEY, override);
    } catch {
      // A one-shot override still works when storage is unavailable.
    }
    return base;
  }
  return "";
}

export const API_BASE_URL = resolveApiBaseUrl();

export function apiUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const target = `${API_BASE_URL}${normalized}`;
  try {
    const expectedOrigin = import.meta.env.DEV
      ? new URL(API_BASE_URL || "/", window.location.origin).origin
      : window.location.origin;
    const parsed = new URL(target, window.location.origin);
    if (parsed.origin !== expectedOrigin || parsed.username || parsed.password) {
      throw new Error("INVALID_API_TARGET");
    }
  } catch {
    throw new Error("INVALID_API_TARGET");
  }
  return target;
}

export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const session = captureSession();
  session.assertCurrent();
  const signal = sessionSignal(init.signal);
  const headers = new Headers(init.headers);
  if (session.identity && session.identity !== "legacy" && !["GET", "HEAD", "OPTIONS"].includes((init.method ?? "GET").toUpperCase())) {
    headers.set("X-CSRF-Token", session.identity.csrfToken);
  }
  const response = guardResponse(await fetch(apiUrl(path), {
    ...init, headers, signal, credentials: "include",
  }), signal);
  session.assertCurrent();
  if (!response.ok) {
    let body = "";
    try {
      body = await response.text();
    } catch {
      // Keep the HTTP status as the useful fallback.
    }
    session.assertCurrent();
    if (response.status === 401) invalidateSession(session.generation);
    const retryAfter = response.headers.get("Retry-After");
    const seconds = retryAfter && /^\d+$/.test(retryAfter) ? Number(retryAfter) : null;
    throw new ApiError(response.status, body, seconds);
  }
  return response;
}

export async function apiJson<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const response = await apiFetch(path, init);
  return (await response.json()) as T;
}

export async function apiPostJson<T>(
  path: string,
  body: unknown,
  init: RequestInit = {},
): Promise<T> {
  return apiJson<T>(path, {
    ...init,
    method: "POST",
    headers: (() => { const headers = new Headers(init.headers); headers.set("Content-Type", "application/json"); return headers; })(),
    body: JSON.stringify(body),
  });
}
